import {
    Button,
    Input,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Skeleton,
} from '@repo/shadcn';
import { format } from 'date-fns';
import { ChevronsUpDown, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useController } from 'react-hook-form';

import {
    API_LIMIT,
    API_PAGE,
    API_SORT_BY,
    API_SORT_ORDER,
} from '@/constants/api-query-params.constant';
import { useLazyGetAllOrdersQuery } from '@/features/slice/order';
import { formatCurrencyInBDT } from '@/utils/currency-formater';

export function OrdersAutoComplete({ control, name }: any) {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const [triggerQuery, { data: searchData, isLoading }] =
        useLazyGetAllOrdersQuery();

    const handleSearchInput = (value: string) => {
        setSearchTerm(value);
    };

    useEffect(() => {
        const delay = setTimeout(() => {
            if (searchTerm.trim().length >= 2) {
                triggerQuery({
                    limit: API_LIMIT.DEFAULT_LIMIT,
                    page: API_PAGE.DEFAULT_PAGE,
                    search: searchTerm.trim() || undefined,
                    sort_order: API_SORT_ORDER.ASCENDING,
                    sort_by: API_SORT_BY.NAME,
                });
            }
        }, 300);

        return () => clearTimeout(delay);
    }, [searchTerm, triggerQuery]);

    const { field } = useController({ name, control });

    const selectedOrder = searchData?.data.find(
        (order: any) => order.id === field.value,
    );

    if (isLoading) {
        return <Skeleton className="mt-2 h-[40px] w-full" />;
    }

    return (
        <div id="portal-root">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between truncate"
                    >
                        <p className="w-[90%] truncate duration-300">
                            {selectedOrder
                                ? `${selectedOrder.invoice_number} - ${formatCurrencyInBDT(selectedOrder?.payment?.total_price)}`
                                : 'Select order...'}
                        </p>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent>
                    <div className="flex items-center border-b px-3">
                        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                        <Input
                            placeholder="Search invoice..."
                            value={searchTerm}
                            onChange={(e) => handleSearchInput(e.target.value)}
                            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                        />
                    </div>
                    <div className="max-h-60 overflow-auto">
                        {searchData?.data.length === 0 ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                                No orders found.
                            </div>
                        ) : (
                            <div>
                                <table className="table-auto gap-4 px-2 py-2 font-bold">
                                    <thead>
                                        <tr>
                                            <th className="px-2">
                                                Invoice No.
                                            </th>
                                            <th className="px-2">Date</th>
                                            <th className="px-2">Time</th>
                                            <th className="px-2">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {searchData?.data.map((order: any) => (
                                            <tr
                                                key={order.id}
                                                onClick={() => {
                                                    field.onChange(order.id);
                                                    setOpen(false);
                                                }}
                                                className="cursor-pointer space-x-2"
                                            >
                                                <td className="px-2">
                                                    {' '}
                                                    {order.invoice_number}
                                                </td>
                                                <td className="px-2">
                                                    {format(
                                                        order.created_at,
                                                        'dd-MMM-yyyy',
                                                    )}
                                                </td>
                                                <td className="px-2">
                                                    {format(
                                                        order.created_at,
                                                        'p',
                                                    )}
                                                </td>

                                                <td className="px-2">
                                                    {formatCurrencyInBDT(
                                                        order.payment
                                                            ?.total_price,
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}

/*

<Command className="relative w-fit">
            <CommandInput
              value={searchTerm}
              onValueChange={handleSearchInput}
              placeholder="Search order..."
              className="truncate"
            />
            <CommandList>
              <CommandEmpty>No order found.</CommandEmpty>
              <CommandGroup>
               
                {searchData?.data?.map((order: any) => (
                  <div>
                    <span> {order.invoice_number}</span>
                    <span>{format(order.created_at, 'dd-MMM-yyyy')}</span>
                    <span>{format(order.created_at, 'p')}</span>
                  </div>
                  /*   <CommandItem
                    key={order.id}
                    value={order.id}
                    onSelect={() => {
                      field.onChange(order.id);
                      setOpen(false);
                    }}
                    className="border-b-[1px] grid grid-cols-4 gap-4"
                  >
                    <span> {order.invoice_number}</span>
                    <span>{format(order.created_at, 'dd-MMM-yyyy')}</span>
                    <span>{format(order.created_at, 'p')}</span>

                    <span>
                      {formatCurrencyInBTD(order.payment?.total_price)}
                    </span>
                  </CommandItem> 
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          
          */
